import { useQuery } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'

/*
 * Os textos de ajuda do observatório, como `GET /api/v1/tooltips` os devolve:
 * uma lista dos tooltips publicados. O texto é conteúdo do portal, guardado no
 * banco para a gestão poder trocá-lo sem build novo. Há dois tipos: o fixo,
 * preso a um local da tela (`local`, como `camada-vc`), e o solto, que a
 * gestão posicionou na área do observatório (`posicao`, em porcentagem).
 *
 * O texto formatado vem em trechos: cada um é texto puro com três marcas
 * (negrito, itálico, sublinhado). Nunca há HTML: a tela monta as marcas como
 * elementos e escreve o texto como texto, então `<b>` dentro de um trecho
 * aparece literal.
 */

/** Um pedaço do texto do tooltip, com as marcas que valem para ele inteiro. */
export interface Trecho {
  texto: string
  negrito: boolean
  italico: boolean
  sublinhado: boolean
}

/** Como o tooltip aparece: no ícone *i* ou num ponto discreto que abre ao passar o mouse. */
export type ModoDoTooltip = 'icone' | 'passar'

/** O tamanho da letra do balão: 11, 12 ou 14px, pelos tokens do balão. */
export type TamanhoDoTooltip = 'menor' | 'padrao' | 'maior'

/** Onde o tooltip solto fica, em porcentagem da área do observatório (0 a 100). */
export interface PosicaoDoTooltip {
  x: number
  y: number
}

/** Um tooltip publicado, já conferido. */
export interface TooltipPublicado {
  /** Nulo só na resposta de antes da gestão, que não trazia o id. */
  id: number | null
  /** O local fixo na tela, ou nulo no tooltip solto. */
  local: string | null
  /** A concatenação dos trechos: serve de nome acessível e de texto puro. */
  texto: string
  trechos: readonly Trecho[]
  modo: ModoDoTooltip
  tamanho: TamanhoDoTooltip
  /** A posição do solto; nula no fixo. */
  posicao: PosicaoDoTooltip | null
}

/** A lista pública separada: os fixos por local e os soltos na ordem da API. */
export interface TooltipsPublicados {
  porLocal: ReadonlyMap<string, TooltipPublicado>
  soltos: readonly TooltipPublicado[]
}

const MODOS: readonly ModoDoTooltip[] = ['icone', 'passar']
const TAMANHOS: readonly TamanhoDoTooltip[] = ['menor', 'padrao', 'maior']
const MARCAS = ['negrito', 'italico', 'sublinhado'] as const

/**
 * O local do texto de cada camada do catálogo, pelo código dela. Camada que não
 * consta aqui (as variáveis ainda sem texto, os estados, os municípios) não
 * tem ícone de ajuda.
 */
const LOCAL_DA_CAMADA: Readonly<Record<string, string>> = {
  'valor-conservacao': 'camada-vc',
  'risco-extincao': 'camada-risco',
  'integridade-ecossistemas': 'camada-integridade',
  carbono: 'camada-carbono',
  'recarga-aguas': 'camada-recarga',
  'vulnerabilidade-climatica': 'camada-clima',
  'custo-oportunidade': 'camada-custo',
  'vc-limites-car': 'camada-limites',
  biomas: 'camada-biomas',
}

export function localDaCamada(codigo: string): string | undefined {
  return LOCAL_DA_CAMADA[codigo]
}

/**
 * Confere os trechos de um item. Marca ausente vale falso (a API completa, mas
 * a leitura não depende disso); trecho sem texto, marca que não é booleana ou
 * lista vazia tiram o item. Chave a mais é ignorada: nada além do texto e das
 * três marcas chega à tela.
 */
export function lerTrechos(valor: unknown): Trecho[] | undefined {
  if (!Array.isArray(valor) || valor.length === 0) return undefined
  const trechos: Trecho[] = []
  for (const item of valor as unknown[]) {
    if (typeof item !== 'object' || item === null) return undefined
    const campos = item as Record<string, unknown>
    if (typeof campos.texto !== 'string') return undefined
    const lido: Trecho = { texto: campos.texto, negrito: false, italico: false, sublinhado: false }
    for (const marca of MARCAS) {
      const v = campos[marca]
      if (v === undefined) continue
      if (typeof v !== 'boolean') return undefined
      lido[marca] = v
    }
    trechos.push(lido)
  }
  return trechos
}

function lerPosicao(valor: unknown): PosicaoDoTooltip | null | undefined {
  if (valor === undefined || valor === null) return null
  if (typeof valor !== 'object') return undefined
  const { x, y } = valor as Record<string, unknown>
  const dentro = (n: unknown): n is number =>
    typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 100
  return dentro(x) && dentro(y) ? { x, y } : undefined
}

function lerItem(item: unknown): TooltipPublicado | undefined {
  if (typeof item !== 'object' || item === null) return undefined
  const campos = item as Record<string, unknown>
  const { id, local, texto } = campos
  if (typeof texto !== 'string') return undefined
  if (local !== undefined && local !== null && typeof local !== 'string') return undefined
  // A resposta de antes da gestão trazia só `{local, texto}`: vale como um
  // trecho sem marca, no ícone e no tamanho padrão.
  const trechos =
    campos.trechos === undefined
      ? [{ texto, negrito: false, italico: false, sublinhado: false }]
      : lerTrechos(campos.trechos)
  if (trechos === undefined) return undefined
  if (texto.trim() === '' || trechos.every((t) => t.texto.trim() === '')) return undefined
  const modo = campos.modo === undefined ? 'icone' : MODOS.find((m) => m === campos.modo)
  const tamanho =
    campos.tamanho === undefined ? 'padrao' : TAMANHOS.find((t) => t === campos.tamanho)
  if (modo === undefined || tamanho === undefined) return undefined
  const fixo = typeof local === 'string'
  // A posição só vale para o solto: o fixo aparece no local dele, e uma posição
  // estranha nele não o tira da tela. Sem local e sem posição, não tem onde
  // aparecer.
  const posicao = fixo ? null : lerPosicao(campos.posicao)
  if (posicao === undefined || (!fixo && posicao === null)) return undefined
  return {
    id: typeof id === 'number' ? id : null,
    local: fixo ? local : null,
    // Os trechos são o texto; o `texto` da resposta é só a soma deles, e é
    // refeito daqui para os dois nunca divergirem na tela.
    texto: trechos.map((t) => t.texto).join(''),
    trechos,
    modo,
    tamanho,
    posicao: fixo ? null : posicao,
  }
}

/**
 * Confere a resposta e separa os fixos (por local) dos soltos. Item fora da
 * forma (sem texto, texto em branco, trechos, modo, tamanho ou posição
 * inválidos, ou sem local e sem posição) fica de fora: um tooltip a menos não
 * tira os outros. Resposta que nem é lista é erro.
 */
export function lerTooltips(corpo: unknown): TooltipsPublicados {
  if (!Array.isArray(corpo))
    throw new Error('Textos de ajuda fora da forma: a resposta não é lista')
  const porLocal = new Map<string, TooltipPublicado>()
  const soltos: TooltipPublicado[] = []
  for (const item of corpo as unknown[]) {
    const lido = lerItem(item)
    if (lido === undefined) continue
    if (lido.local === null) soltos.push(lido)
    else porLocal.set(lido.local, lido)
  }
  return { porLocal, soltos }
}

/** A lista de textos, pedida uma vez e compartilhada por todos os ícones da tela. */
export function useTooltips() {
  return useQuery({
    queryKey: ['tooltips'],
    queryFn: async () => lerTooltips(await apiFetch<unknown>('/v1/tooltips')),
  })
}

/**
 * O texto de ajuda de um local, ou `undefined` enquanto a lista carrega, se a
 * rota falhou ou se não há texto para o local: nos três casos o ícone não
 * aparece.
 */
export function useTextoDeAjuda(local: string | undefined): string | undefined {
  const { data } = useTooltips()
  return local === undefined ? undefined : data?.porLocal.get(local)?.texto
}
